import { EllipsisVertical, Headset, Merge, Pencil, Split, UserRound } from "lucide-mui";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type MouseEvent } from "react";
import { api, getErrorMessage } from "../../../../api/client.ts";
import type {
  ClientTag,
  Connection,
  CrmRep,
  CrmRepIdentity,
  OwnerDomain,
  Tenant,
} from "../../../../api/client.ts";
import { keys } from "../../../../api/queryKeys.ts";
import CrmProviderLogo from "../../../../components/CrmProviderLogo.tsx";
import DropdownMenu from "../../../../components/DropdownMenu.tsx";
import EmptyTableRow from "../../../../components/table/EmptyTableRow.tsx";
import ScrollTable from "../../../../components/table/ScrollTable.tsx";
import { formatNumber } from "../../../../utils/format.ts";

interface CrmPeopleDialogProps {
  connection: Connection | null;
  tenants: Tenant[] | undefined;
  tenantName: (tid: string) => string;
  onClose: () => void;
}

/**
 * Who is behind a connection's activity, and which client each login works
 * for. Both are derived on sync; this is where a wrong link gets corrected.
 */
export default function CrmPeopleDialog({
  connection,
  tenants,
  tenantName,
  onClose,
}: CrmPeopleDialogProps) {
  const [tab, setTab] = useState(0);
  return (
    <Dialog open={!!connection} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          {connection && <CrmProviderLogo provider={connection.provider} />}
          <span>People & clients — {connection?.name || connection?.provider}</span>
        </Stack>
      </DialogTitle>
      <Tabs value={tab} onChange={(_, v: number) => setTab(v)} sx={{ px: 3 }}>
        <Tab label="People" />
        <Tab label="Client by login" />
        <Tab label="Client tags" />
      </Tabs>
      <Divider />
      <DialogContent sx={{ minHeight: 360 }}>
        {connection && tab === 0 && <PeopleTab connectionId={connection.id} />}
        {connection && tab === 1 && (
          <LoginDomainsTab connectionId={connection.id} tenants={tenants} tenantName={tenantName} />
        )}
        {connection && tab === 2 && (
          <ClientTagsTab connectionId={connection.id} tenants={tenants} tenantName={tenantName} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function PeopleTab({ connectionId }: { connectionId: string }) {
  const queryClient = useQueryClient();
  const reps = useQuery({
    queryKey: keys.crmIntegration.reps(connectionId),
    queryFn: () => api.getCrmReps(connectionId),
  });
  const [renaming, setRenaming] = useState<CrmRep | null>(null);
  const [merging, setMerging] = useState<CrmRep | null>(null);
  const [moving, setMoving] = useState<{ identity: CrmRepIdentity; anchor: HTMLElement } | null>(
    null,
  );

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: keys.crmIntegration.reps(connectionId) });

  const move = useMutation({
    mutationFn: ({ identityId, repId }: { identityId: string; repId: string | null }) =>
      api.moveCrmRepIdentity(connectionId, identityId, repId),
    onSuccess: refresh,
  });

  const list = reps.data ?? [];

  return (
    <>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Everyone whose calls appear in this CRM, across their logins and the names the dialer logs
        for them. People are linked automatically on every sync; click a login or dialer name to
        move it if a link is wrong.
      </Typography>
      {move.isError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => move.reset()}>
          {getErrorMessage(move.error)}
        </Alert>
      )}
      {reps.isLoading ? (
        <CircularProgress size={24} />
      ) : (
        <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell>Person</TableCell>
              <TableCell>Logins and dialer names</TableCell>
              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                Calls (90 d)
              </TableCell>
              <TableCell width={48} />
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map((rep) => (
              <TableRow key={rep.id}>
                <TableCell sx={{ fontWeight: 500, whiteSpace: "nowrap" }}>
                  {rep.display_name}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", rowGap: 0.5 }}>
                    {rep.identities.map((identity) => (
                      <IdentityChip
                        key={identity.id}
                        identity={identity}
                        onClick={(e) => setMoving({ identity, anchor: e.currentTarget })}
                      />
                    ))}
                  </Stack>
                </TableCell>
                <TableCell align="right">{formatNumber(rep.calls_90d)}</TableCell>
                <TableCell>
                  <DropdownMenu
                    aria-label={`Actions for ${rep.display_name}`}
                    icon={<EllipsisVertical fontSize="small" />}
                    align="right"
                  >
                    <MenuItem onClick={() => setRenaming(rep)}>
                      <ListItemIcon>
                        <Pencil fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Rename</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => setMerging(rep)} disabled={list.length < 2}>
                      <ListItemIcon>
                        <Merge fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Merge into…</ListItemText>
                    </MenuItem>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {list.length === 0 && (
              <EmptyTableRow colSpan={4} align="left">
                No people yet — they appear after the next sync.
              </EmptyTableRow>
            )}
          </TableBody>
        </ScrollTable>
      )}

      <Menu
        anchorEl={moving?.anchor}
        open={!!moving}
        onClose={() => setMoving(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { mt: 0.5, maxHeight: 420 } } }}
      >
        <MenuItem
          onClick={() => {
            if (moving) move.mutate({ identityId: moving.identity.id, repId: null });
            setMoving(null);
          }}
        >
          <ListItemIcon>
            <Split fontSize="small" />
          </ListItemIcon>
          <ListItemText>Split off as its own person</ListItemText>
        </MenuItem>
        <ListSubheader>Move to</ListSubheader>
        {list
          .filter((rep) => !rep.identities.some((i) => i.id === moving?.identity.id))
          .map((rep) => (
            <MenuItem
              key={rep.id}
              onClick={() => {
                if (moving) move.mutate({ identityId: moving.identity.id, repId: rep.id });
                setMoving(null);
              }}
            >
              {rep.display_name}
            </MenuItem>
          ))}
      </Menu>

      <RenameRepDialog
        connectionId={connectionId}
        rep={renaming}
        onClose={() => setRenaming(null)}
        onDone={refresh}
      />
      <MergeRepDialog
        connectionId={connectionId}
        rep={merging}
        reps={list}
        onClose={() => setMerging(null)}
        onDone={refresh}
      />
    </>
  );
}

function IdentityChip({
  identity,
  onClick,
}: {
  identity: CrmRepIdentity;
  onClick: (e: MouseEvent<HTMLElement>) => void;
}) {
  const isLogin = identity.kind === "owner";
  const chip = (
    <Chip
      size="small"
      variant={identity.source === "manual" ? "filled" : "outlined"}
      icon={isLogin ? <UserRound fontSize="small" /> : <Headset fontSize="small" />}
      label={identity.label}
      onClick={onClick}
    />
  );
  const kind = isLogin ? "CRM login" : "Name logged by the dialer";
  return (
    <Tooltip title={identity.source === "manual" ? `${kind} · linked by hand` : kind}>
      {chip}
    </Tooltip>
  );
}

function RenameRepDialog({
  connectionId,
  rep,
  onClose,
  onDone,
}: {
  connectionId: string;
  rep: CrmRep | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [name, setName] = useState("");
  const rename = useMutation({
    mutationFn: () => api.renameCrmRep(connectionId, rep!.id, name.trim()),
    onSuccess: () => {
      onDone();
      onClose();
    },
  });
  return (
    <Dialog
      open={!!rep}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      slotProps={{ transition: { onEnter: () => setName(rep?.display_name ?? "") } }}
    >
      <DialogTitle>Rename person</DialogTitle>
      <DialogContent>
        {rename.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {getErrorMessage(rename.error)}
          </Alert>
        )}
        <TextField
          autoFocus
          fullWidth
          size="small"
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!name.trim()}
          loading={rename.isPending}
          onClick={() => rename.mutate()}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function MergeRepDialog({
  connectionId,
  rep,
  reps,
  onClose,
  onDone,
}: {
  connectionId: string;
  rep: CrmRep | null;
  reps: CrmRep[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [into, setInto] = useState("");
  const merge = useMutation({
    mutationFn: () => api.mergeCrmRep(connectionId, rep!.id, into),
    onSuccess: () => {
      onDone();
      onClose();
    },
  });
  return (
    <Dialog
      open={!!rep}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      slotProps={{ transition: { onEnter: () => setInto("") } }}
    >
      <DialogTitle>Merge {rep?.display_name}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          All of {rep?.display_name}'s logins and dialer names move to the person you pick, and
          their calls are counted there.
        </Typography>
        {merge.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {getErrorMessage(merge.error)}
          </Alert>
        )}
        <TextField
          select
          fullWidth
          size="small"
          label="Merge into"
          value={into}
          onChange={(e) => setInto(e.target.value)}
        >
          {reps
            .filter((r) => r.id !== rep?.id)
            .map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.display_name}
              </MenuItem>
            ))}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!into}
          loading={merge.isPending}
          onClick={() => merge.mutate()}
        >
          Merge
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const NOT_A_CLIENT = "";

function LoginDomainsTab({
  connectionId,
  tenants,
  tenantName,
}: {
  connectionId: string;
  tenants: Tenant[] | undefined;
  tenantName: (tid: string) => string;
}) {
  const queryClient = useQueryClient();
  const domains = useQuery({
    queryKey: keys.crmIntegration.ownerDomains(connectionId),
    queryFn: () => api.getOwnerDomains(connectionId),
  });

  const save = useMutation({
    mutationFn: async ({ domain, tenantId }: { domain: string; tenantId: string }) => {
      if (tenantId === NOT_A_CLIENT) await api.deleteOwnerDomain(connectionId, domain);
      else await api.setOwnerDomain(connectionId, domain, tenantId);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: keys.crmIntegration.ownerDomains(connectionId) }),
  });

  const list = domains.data ?? [];

  return (
    <>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        A login on a client's domain (sun@lawai.com) works for that client, so its calls and
        contacts are attributed to the client ahead of any client tags on the contact. Logins on
        your own domain say nothing about the client — leave them unmapped.
      </Typography>
      {save.isError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => save.reset()}>
          {getErrorMessage(save.error)}
        </Alert>
      )}
      {domains.isLoading ? (
        <CircularProgress size={24} />
      ) : (
        <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell>Login domain</TableCell>
              <TableCell>Logins</TableCell>
              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                Calls (90 d)
              </TableCell>
              <TableCell width={220}>Client</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map((d) => (
              <DomainRow
                key={d.email_domain}
                domain={d}
                tenants={tenants}
                tenantName={tenantName}
                disabled={save.isPending}
                onChange={(tenantId) => save.mutate({ domain: d.email_domain, tenantId })}
              />
            ))}
            {list.length === 0 && (
              <EmptyTableRow colSpan={4} align="left">
                No logins synced yet.
              </EmptyTableRow>
            )}
          </TableBody>
        </ScrollTable>
      )}

      <ReapplyNotice connectionId={connectionId} />
    </>
  );
}

function DomainRow({
  domain: d,
  tenants,
  tenantName,
  disabled,
  onChange,
}: {
  domain: OwnerDomain;
  tenants: Tenant[] | undefined;
  tenantName: (tid: string) => string;
  disabled: boolean;
  onChange: (tenantId: string) => void;
}) {
  const known = !d.tenant_id || (tenants ?? []).some((t) => t.id === d.tenant_id);
  return (
    <TableRow>
      <TableCell sx={{ fontFamily: "monospace" }}>{d.email_domain}</TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {d.logins.join(", ")}
        </Typography>
      </TableCell>
      <TableCell align="right">{formatNumber(d.calls_90d)}</TableCell>
      <TableCell>
        <TextField
          select
          fullWidth
          size="small"
          value={d.tenant_id ?? NOT_A_CLIENT}
          disabled={disabled}
          slotProps={{ select: { displayEmpty: true } }}
          onChange={(e) => onChange(e.target.value)}
        >
          <MenuItem value={NOT_A_CLIENT}>
            <em>Not a client</em>
          </MenuItem>
          {!known && d.tenant_id && (
            <MenuItem value={d.tenant_id}>{tenantName(d.tenant_id)}</MenuItem>
          )}
          {tenants?.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.name}
            </MenuItem>
          ))}
        </TextField>
      </TableCell>
    </TableRow>
  );
}

/** Mapping changes reach records as they sync; this re-derives existing ones now. */
function ReapplyNotice({ connectionId }: { connectionId: string }) {
  const reapply = useMutation({ mutationFn: () => api.reassignTenants(connectionId) });
  return (
      <Box sx={{ mt: 3 }}>
        {reapply.isSuccess ? (
          <Alert severity="success" onClose={() => reapply.reset()}>
            Clients re-derived for every record in this connection.
          </Alert>
        ) : reapply.isError ? (
          <Alert severity="error" onClose={() => reapply.reset()}>
            {getErrorMessage(reapply.error)}
          </Alert>
        ) : (
          <Alert
            severity="info"
            action={
              <Button
                color="inherit"
                size="small"
                loading={reapply.isPending}
                onClick={() => reapply.mutate()}
              >
                Re-apply now
              </Button>
            }
          >
            Changes apply to records as they sync. Re-apply to update existing records too.
          </Alert>
        )}
      </Box>
  );
}

/** Not a select value a tenant id can take: the tag is not mapped yet (seeded on sync). */
const TAG_UNMAPPED = "__unmapped";

function ClientTagsTab({
  connectionId,
  tenants,
  tenantName,
}: {
  connectionId: string;
  tenants: Tenant[] | undefined;
  tenantName: (tid: string) => string;
}) {
  const queryClient = useQueryClient();
  const tags = useQuery({
    queryKey: keys.crmIntegration.clientTags(connectionId),
    queryFn: () => api.getClientTags(connectionId),
  });
  const save = useMutation({
    mutationFn: async ({ tag, value }: { tag: ClientTag; value: string }) => {
      if (value === TAG_UNMAPPED) await api.deleteClientTag(connectionId, tag.tag);
      else await api.setClientTag(connectionId, tag, value === NOT_A_CLIENT ? null : value);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: keys.crmIntegration.clientTags(connectionId) }),
  });
  const list = tags.data ?? [];

  return (
    <>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        The CRM's Client field lists the clients a company or contact is worked for. Each value
        names one client: a record tagged with several belongs to each of them. Tags are matched to
        clients by name as they first appear; correct any the name did not find (NNL for Nordic
        NeuroLab).
      </Typography>
      {save.isError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => save.reset()}>
          {getErrorMessage(save.error)}
        </Alert>
      )}
      {tags.isLoading ? (
        <CircularProgress size={24} />
      ) : (
        <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell>Tag</TableCell>
              <TableCell align="right">Companies</TableCell>
              <TableCell align="right">Contacts</TableCell>
              <TableCell width={240}>Client</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map((t) => {
              const value =
                t.source === null ? TAG_UNMAPPED : (t.tenant_id ?? NOT_A_CLIENT);
              const known = !t.tenant_id || (tenants ?? []).some((x) => x.id === t.tenant_id);
              return (
                <TableRow key={t.tag}>
                  <TableCell>{t.label}</TableCell>
                  <TableCell align="right">{formatNumber(t.companies)}</TableCell>
                  <TableCell align="right">{formatNumber(t.contacts)}</TableCell>
                  <TableCell>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={value}
                      disabled={save.isPending}
                      onChange={(e) => save.mutate({ tag: t, value: e.target.value })}
                    >
                      <MenuItem value={TAG_UNMAPPED}>
                        <em>Not mapped</em>
                      </MenuItem>
                      <MenuItem value={NOT_A_CLIENT}>
                        <em>Not a client</em>
                      </MenuItem>
                      {!known && t.tenant_id && (
                        <MenuItem value={t.tenant_id}>{tenantName(t.tenant_id)}</MenuItem>
                      )}
                      {tenants?.map((x) => (
                        <MenuItem key={x.id} value={x.id}>
                          {x.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                </TableRow>
              );
            })}
            {list.length === 0 && (
              <EmptyTableRow colSpan={4} align="left">
                No client tags on this connection's records.
              </EmptyTableRow>
            )}
          </TableBody>
        </ScrollTable>
      )}
      <ReapplyNotice connectionId={connectionId} />
    </>
  );
}
